// frontend/src/components/OAuthCallback.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { TokenStorage } from '../utils/api';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      // Store the token
      TokenStorage.setToken(token);

      // Detect browser timezone and update user profile if needed
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log('[OAuthCallback] ========== TIMEZONE UPDATE START ==========');
        console.log('[OAuthCallback] Detected browser timezone:', browserTz);
        
        // Check if user has a timezone set
        console.log('[OAuthCallback] Fetching user profile...');
        const profileRes = await api.get('/api/user/me');
        console.log('[OAuthCallback] User profile response:', JSON.stringify(profileRes.data, null, 2));
        console.log('[OAuthCallback] Current user timezone:', profileRes.data.user.timezone);

        // Store firstName in sessionStorage for Welcome page personalization
        try {
          const firstName = profileRes.data.user.first_name || '';
          if (firstName) {
            sessionStorage.setItem('firstName', firstName);
            console.log('[OAuthCallback] Stored firstName in sessionStorage:', firstName);
          }
        } catch (storageErr) {
          console.warn('[OAuthCallback] sessionStorage blocked (private browsing?):', storageErr);
          // Non-critical, continue anyway
        }

        // Update timezone if not set or if it's the default UTC
        const currentTz = profileRes.data.user.timezone;
        const needsUpdate = !currentTz || currentTz === 'UTC';
        
        if (needsUpdate) {
          console.log('[OAuthCallback] Timezone needs update (current:', currentTz, '), updating to:', browserTz);
          console.log('[OAuthCallback] Calling PUT /api/user/me/timezone with body:', { timezone: browserTz });
          
          const updateRes = await api.put('/api/user/me/timezone', { timezone: browserTz });
          console.log('[OAuthCallback] Timezone update response:', JSON.stringify(updateRes.data, null, 2));
          console.log('[OAuthCallback] Timezone update SUCCESS');
        } else {
          console.log('[OAuthCallback] User already has valid timezone set:', currentTz, '- skipping update');
        }
        console.log('[OAuthCallback] ========== TIMEZONE UPDATE END ==========');
      } catch (err) {
        console.error('[OAuthCallback] ========== TIMEZONE UPDATE FAILED ==========');
        console.error('[OAuthCallback] Error object:', err);
        console.error('[OAuthCallback] Error response:', err.response?.data);
        console.error('[OAuthCallback] Error status:', err.response?.status);
        console.error('[OAuthCallback] Error stack:', err.stack);
        // Non-critical, continue anyway
      }

      // Fetch onboarding step to determine where to redirect
      try {
        const { data } = await api.get('/api/user/onboarding');
        const step = data?.step;
        
        console.log('[OAuthCallback] Onboarding step:', step);

        // Route map matching LoginPage logic
        const routeMap = {
          welcome:          '/welcome',
          connect_bank:     '/connect-bank',
          review_detected:  '/review-detected',
          complete:         '/dashboard'
        };

        const destination = routeMap[step] || '/dashboard';
        console.log('[OAuthCallback] Redirecting to:', destination);
        navigate(destination, { replace: true });
      } catch (err) {
        console.error('[OAuthCallback] Failed to fetch onboarding step:', err);
        // If onboarding fetch fails, assume new user and start at beginning
        navigate('/connect-bank', { replace: true });
      }
    };

    handleOAuthCallback();
  }, [location, navigate]);

  return (
    <div style={styles.container}>
      <h2>Signing you in…</h2>
      {error && <p style={styles.error}>Something went wrong. Redirecting to login...</p>}
    </div>
  );
}

const styles = {
  container: {
    marginTop: '100px',
    textAlign: 'center'
  },
  error: {
    color: '#e74c3c',
    marginTop: '20px'
  }
};
